'use client'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/web/components'
import { SCHOOL_GRADE_OPTIONS } from '@helper/utils/domain'

export interface SchoolGradeSelectProps {
  /** Stored grade value (e.g. `kelas_1`), or null when no grade is assigned. */
  value: string | null
  onValueChange: (value: string | null) => void
  disabled?: boolean
  className?: string
  placeholder?: string
}

/**
 * Select for a student's grade level (Kelas 0–6) — the numeric `user.schoolClass`
 * field. Distinct from `SchoolClassSelect`, which assigns a team (class group).
 *
 * The grade is optional, so pressing the already-selected row clears it back to empty instead of
 * re-picking it. Two Base UI facts make that a plain usage-site toggle rather than an extra
 * "Belum dipilih" row or a clear button bolted onto the trigger: `Select.Root` types `value` as
 * `Value | null` and renders the placeholder for null, and `setValue` calls `onValueChange` on
 * every item press with no equality guard — so a press on the current value still arrives here.
 */
export function SchoolGradeSelect({
  value,
  onValueChange,
  disabled,
  className,
  placeholder = 'Pilih kelas',
}: SchoolGradeSelectProps) {
  return (
    <Select
      value={value}
      onValueChange={(next: string | null) => onValueChange(next === value ? null : next)}
      disabled={disabled}
    >
      <SelectTrigger className={className}>
        {/* Children win over `placeholder`, so the null case has to name the placeholder itself. */}
        <SelectValue placeholder={placeholder}>
          {(current: string | null) =>
            (current && SCHOOL_GRADE_OPTIONS.find((option) => option.value === current)?.label) || placeholder
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {SCHOOL_GRADE_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
